<template>
  <v-dialog v-model="dialogVisible" max-width="1000px" persistent scrollable>
    <v-card class="rounded-xl overflow-hidden elevation-2">
      <!-- Header -->
      <v-card-title class="pa-4 bg-white border-bottom d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-cog-box</v-icon>
          <div>
            <span class="font-weight-bold text-h6 grey--text text--darken-4">
              Mantenimiento de Capacidades TM/FCL
            </span>
            <div class="caption grey--text text--darken-1">
              Configuración de equivalencias maestras de capacidad de contenedor por producto y presentación
            </div>
          </div>
        </div>
        <v-btn icon small @click="closeModal">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Body / Grilla y Formulario -->
      <v-card-text class="pa-4 bg-grey-lighten-5">
        <!-- Barra de Búsqueda y Botón Nueva Configuración -->
        <div class="d-flex align-center justify-space-between mb-3 flex-wrap gap-2">
          <div class="d-flex align-center" style="max-width: 500px; width: 100%;">
            <s-select-definition
              v-model="filterDivision"
              :def="1175"
              label="Filtrar División"
              clearable
              dense
              hide-details
              class="mr-2"
              style="max-width: 180px;"
              @input="loadData"
            />
            <s-text
              v-model="filterSearch"
              label="Buscar producto o empaque..."
              append-icon="mdi-magnify"
              dense
              hide-details
              clearable
              @input="onSearchInput"
            />
          </div>

          <v-btn
            color="primary"
            small
            class="text-capitalize rounded-lg px-3 font-weight-bold"
            @click="openNewForm"
          >
            <v-icon left small>mdi-plus</v-icon>
            Nueva Capacidad
          </v-btn>
        </div>

        <!-- Formulario Inline / Drawer para Crear o Editar -->
        <v-expand-transition>
          <v-card v-if="showForm" class="rounded-xl border-light bg-white pa-4 mb-4" elevation="1">
            <div class="subtitle-2 font-weight-bold grey--text text--darken-3 mb-3 d-flex align-center justify-space-between">
              <span>{{ formData.CpcID > 0 ? 'Editar Capacidad #' + formData.CpcID : 'Nueva Configuración de Capacidad' }}</span>
              <v-btn icon x-small @click="cancelForm">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>

            <v-row dense>
              <!-- División (Catálogo 1175) -->
              <v-col cols="12" sm="3">
                <s-select-definition
                  v-model="formData.TypeDestination"
                  :def="1175"
                  label="División *"
                  dense
                  hide-details
                />
              </v-col>

              <!-- Artículo SAP (Producto) con s-article-search -->
              <v-col cols="12" sm="6">
                <s-article-search
                  label="Artículo SAP (Producto) *"
                  v-model="selectedArticle"
                  clearable
                  placeholder="Buscar artículo en SAP por código o nombre..."
                  @selected="onArticleSelected"
                  @input="onArticleInput"
                />
              </v-col>

              <!-- Código de Artículo SAP (Autocompletado) -->
              <v-col cols="12" sm="3">
                <s-text
                  :value="formData.CpcSapCode || 'Sin código'"
                  label="Cód. Artículo SAP"
                  readonly
                  disabled
                  dense
                  hide-details
                />
              </v-col>

              <!-- Empaque / Presentación (Catálogo 1341) -->
              <v-col cols="12" sm="4">
                <s-select-definition
                  v-model="selectedPackagingDef"
                  :def="1341"
                  label="Empaque / Presentación *"
                  return-object
                  @input="onPackagingSelected"
                  dense
                  hide-details
                />
              </v-col>

              <!-- Capacidad TM/FCL -->
              <v-col cols="12" sm="4">
                <s-text
                  v-model.number="formData.CpcCapacityTonsFcl"
                  label="Capacidad TM por FCL *"
                  decimal
                  placeholder="ej: 21.60"
                  dense
                  hide-details
                />
              </v-col>

              <!-- Vigencia Desde -->
              <v-col cols="12" sm="2">
                <s-date
                  v-model="formData.CpcValidityFrom"
                  label="Vigencia Desde"
                  dense
                  hide-details
                />
              </v-col>

              <!-- Vigencia Hasta -->
              <v-col cols="12" sm="2">
                <s-date
                  v-model="formData.CpcValidityTo"
                  label="Vigencia Hasta"
                  dense
                  hide-details
                />
              </v-col>

              <!-- Observación -->
              <v-col cols="12">
                <s-text
                  v-model="formData.CpcObservation"
                  label="Observación / Detalle (Opcional)"
                  placeholder="Nota informativa de la equivalencia..."
                  dense
                  hide-details
                />
              </v-col>
            </v-row>

            <div class="d-flex justify-end mt-3 gap-2">
              <v-btn text small class="text-capitalize" @click="cancelForm">
                Cancelar
              </v-btn>
              <v-btn
                color="primary"
                small
                class="text-capitalize rounded-lg px-4 font-weight-bold"
                :loading="saving"
                @click="saveCapacity"
              >
                Guardar Configuración
              </v-btn>
            </div>
          </v-card>
        </v-expand-transition>

        <!-- Tabla de Capacidades -->
        <v-card class="rounded-xl border-light bg-white overflow-hidden" elevation="0">
          <v-data-table
            :headers="tableHeaders"
            :items="capacityList"
            :loading="loading"
            loading-text="Cargando configuraciones de capacidad..."
            no-data-text="No hay configuraciones registradas"
            dense
            :items-per-page="10"
            class="elevation-0"
          >
            <!-- División -->
            <template v-slot:item.TypeDestination="{ item }">
              <v-chip
                x-small
                class="font-weight-bold"
                :color="item.TypeDestination === 1 ? 'green lighten-5 green--text text--darken-3' : 'blue lighten-5 blue--text text--darken-3'"
              >
                {{ item.TypeDestination === 1 ? 'Frescos' : 'IQF / Congelados' }}
              </v-chip>
            </template>

            <!-- Producto -->
            <template v-slot:item.EpdProduct="{ item }">
              <span class="font-weight-bold grey--text text--darken-4">{{ item.EpdProduct }}</span>
              <span v-if="item.CpcSapCode" class="caption grey--text ml-1">({{ item.CpcSapCode }})</span>
            </template>

            <!-- Capacidad TM/FCL -->
            <template v-slot:item.CpcCapacityTonsFcl="{ item }">
              <strong class="primary--text font-weight-bold">
                {{ Number(item.CpcCapacityTonsFcl).toFixed(2) }} TM
              </strong>
            </template>

            <!-- Vigencia -->
            <template v-slot:item.vigencia="{ item }">
              <span class="caption grey--text text--darken-2" v-if="item.CpcValidityFrom || item.CpcValidityTo">
                {{ item.CpcValidityFrom ? item.CpcValidityFrom.substring(0, 10) : '—' }} al {{ item.CpcValidityTo ? item.CpcValidityTo.substring(0, 10) : 'Indefinido' }}
              </span>
              <span class="caption grey--text" v-else>Siempre vigente</span>
            </template>

            <!-- Estado -->
            <template v-slot:item.SecStatus="{ item }">
              <v-chip x-small :color="item.SecStatus === 1 ? 'green lighten-5' : 'grey lighten-4'" :text-color="item.SecStatus === 1 ? 'green darken-3' : 'grey darken-1'">
                {{ item.SecStatus === 1 ? 'Activo' : 'Inactivo' }}
              </v-chip>
            </template>

            <!-- Acciones -->
            <template v-slot:item.actions="{ item }">
              <div class="d-flex align-center">
                <v-btn icon x-small color="primary" @click="editCapacity(item)" title="Editar">
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  v-if="item.SecStatus === 1"
                  icon
                  x-small
                  color="error"
                  @click="deactivateCapacity(item)"
                  title="Desactivar"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-card-text>

      <!-- Footer -->
      <v-card-actions class="pa-3 bg-white border-top justify-end">
        <v-btn text small class="text-capitalize" @click="closeModal">
          Cerrar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import CommercialCapacityConfigService from "@/services/ExternalTrade/CommercialCapacityConfigService";
import sArticleSearch from "@/components/Utils/Logistics/SArticleSearch";

export default {
  name: "CommercialCapacityConfigModal",
  components: {
    sArticleSearch,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    defaultDivision: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      capacityList: [],
      filterDivision: null,
      filterSearch: "",
      showForm: false,
      selectedArticle: null,
      selectedPackagingDef: null,
      formData: {
        CpcID: 0,
        TypeDestination: 1,
        CpcSapCode: "",
        EpdProduct: "",
        DedPackagingID: null,
        EpdPackaging: "",
        CpcCapacityTonsFcl: null,
        CpcValidityFrom: null,
        CpcValidityTo: null,
        CpcObservation: "",
        SecStatus: 1,
      },
      tableHeaders: [
        { text: "División", value: "TypeDestination", width: "120px" },
        { text: "Producto", value: "EpdProduct" },
        { text: "Empaque / Presentación", value: "EpdPackaging" },
        { text: "Capacidad (TM/FCL)", value: "CpcCapacityTonsFcl", align: "end", width: "150px" },
        { text: "Vigencia", value: "vigencia", width: "180px" },
        { text: "Estado", value: "SecStatus", align: "center", width: "90px" },
        { text: "Acciones", value: "actions", sortable: false, width: "80px", align: "center" },
      ],
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  watch: {
    value(val) {
      if (val) {
        if (this.defaultDivision) {
          this.filterDivision = this.defaultDivision;
        }
        this.loadData();
      }
    },
  },
  methods: {
    closeModal() {
      this.dialogVisible = false;
      this.showForm = false;
    },
    async loadData() {
      this.loading = true;
      try {
        const filter = {
          TypeDestination: this.filterDivision ? this.filterDivision : null,
          Search: this.filterSearch ? this.filterSearch.trim() : null,
          SecStatus: null, // Ver tanto activas como inactivas
          OnlyActiveValidity: false,
        };
        const requestID = this.$fun.getUserID();
        const res = await CommercialCapacityConfigService.list(filter, requestID);
        if (res && res.data) {
          this.capacityList = res.data;
        }
      } catch (e) {
        console.error("Error al cargar configuraciones de capacidad:", e);
        this.$fun.alert("Error al cargar la lista de capacidades.", "error");
      } finally {
        this.loading = false;
      }
    },
    onSearchInput: _.debounce(function () {
      this.loadData();
    }, 400),
    onArticleSelected(art) {
      if (art) {
        this.formData.CpcSapCode = art.ArtCode || "";
        this.formData.EpdProduct = art.ArtName || "";
      } else {
        this.formData.CpcSapCode = "";
        this.formData.EpdProduct = "";
      }
    },
    onArticleInput(art) {
      if (!art) {
        this.formData.CpcSapCode = "";
        this.formData.EpdProduct = "";
      }
    },
    onPackagingSelected(pkg) {
      if (pkg) {
        this.formData.DedPackagingID = pkg.DedID || null;
        this.formData.EpdPackaging = pkg.DedDescription || pkg.text || "";
      } else {
        this.formData.DedPackagingID = null;
        this.formData.EpdPackaging = "";
      }
    },
    openNewForm() {
      this.selectedArticle = null;
      this.selectedPackagingDef = null;
      this.formData = {
        CpcID: 0,
        TypeDestination: this.filterDivision ? this.filterDivision : 1,
        CpcSapCode: "",
        EpdProduct: "",
        DedPackagingID: null,
        EpdPackaging: "",
        CpcCapacityTonsFcl: null,
        CpcValidityFrom: null,
        CpcValidityTo: null,
        CpcObservation: "",
        SecStatus: 1,
      };
      this.showForm = true;
    },
    editCapacity(item) {
      this.selectedArticle = item.EpdProduct || item.CpcSapCode || null;
      this.selectedPackagingDef = item.DedPackagingID ? { DedID: item.DedPackagingID, DedDescription: item.EpdPackaging } : item.EpdPackaging;
      this.formData = {
        CpcID: item.CpcID,
        TypeDestination: item.TypeDestination,
        CpcSapCode: item.CpcSapCode || "",
        EpdProduct: item.EpdProduct,
        DedPackagingID: item.DedPackagingID,
        EpdPackaging: item.EpdPackaging,
        CpcCapacityTonsFcl: item.CpcCapacityTonsFcl,
        CpcValidityFrom: item.CpcValidityFrom ? item.CpcValidityFrom.substring(0, 10) : null,
        CpcValidityTo: item.CpcValidityTo ? item.CpcValidityTo.substring(0, 10) : null,
        CpcObservation: item.CpcObservation || "",
        SecStatus: item.SecStatus,
      };
      this.showForm = true;
    },
    cancelForm() {
      this.showForm = false;
    },
    async saveCapacity() {
      if (!this.formData.EpdProduct || !this.formData.EpdProduct.trim()) {
        this.$fun.alert("Debe ingresar el nombre del producto.", "warning");
        return;
      }
      if (!this.formData.EpdPackaging || !this.formData.EpdPackaging.trim()) {
        this.$fun.alert("Debe seleccionar o ingresar el empaque/presentación.", "warning");
        return;
      }
      if (!this.formData.CpcCapacityTonsFcl || this.formData.CpcCapacityTonsFcl <= 0) {
        this.$fun.alert("La capacidad TM por FCL debe ser mayor a cero.", "warning");
        return;
      }

      this.saving = true;
      try {
        const payload = {
          ...this.formData,
          EpdProduct: this.formData.EpdProduct.trim().toUpperCase(),
          EpdPackaging: this.formData.EpdPackaging.trim().toUpperCase(),
        };
        const requestID = this.$fun.getUserID();
        const res = await CommercialCapacityConfigService.save(payload, requestID);
        if (res && res.data) {
          this.$fun.alert("Configuración de capacidad guardada con éxito.", "success");
          this.showForm = false;
          this.loadData();
          this.$emit("config-updated");
        }
      } catch (e) {
        console.error("Error al guardar capacidad:", e);
        const msg = e.response?.data?.Message || e.response?.data || e.message || "Error al guardar.";
        this.$fun.alert(msg, "error");
      } finally {
        this.saving = false;
      }
    },
    async deactivateCapacity(item) {
      const confirm = await this.$fun.alert(
        `¿Está seguro de desactivar la capacidad para ${item.EpdProduct} - ${item.EpdPackaging}?`,
        "question"
      );
      if (!confirm || !confirm.value) return;

      try {
        const payload = {
          ...item,
          SecStatus: 0,
        };
        const requestID = this.$fun.getUserID();
        await CommercialCapacityConfigService.save(payload, requestID);
        this.$fun.alert("Configuración desactivada.", "success");
        this.loadData();
        this.$emit("config-updated");
      } catch (e) {
        console.error("Error al desactivar capacidad:", e);
        this.$fun.alert("Error al desactivar configuración.", "error");
      }
    },
  },
};
</script>
